import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { exploreSampleAndGo } from "@/lib/sampleData";
import { logEvent } from "@/lib/analytics";
import { PageHeader } from "@/components/ui-bits";
import { InsightsPanel } from "@/components/InsightsPanel";
import { ValidateStep, ImportHealthCard, saveImportHealth } from "@/components/SmartImportAssistant";
import { fmtNum } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import {
  UploadCloud, FileSpreadsheet, ArrowRight, ArrowLeft, Check, AlertTriangle,
  CheckCircle2, LayoutDashboard, Loader2, FileDown, Save, Clock, RefreshCw,
  Target, HeartPulse, TrendingUp, Handshake, BarChart3, ListChecks, Lock,
  FolderOpen, Sparkles, Plus,
} from "lucide-react";

const OUTCOMES = [
  { icon: Target, title: "Money Stuck Analyzer™", desc: "See exactly how much is recoverable and what's at risk." },
  { icon: HeartPulse, title: "Recovery Health Score", desc: "A single score that grades your portfolio health." },
  { icon: TrendingUp, title: "Priority Accounts", desc: "Auto-ranked accounts your team should chase first." },
  { icon: Handshake, title: "Settlement Opportunities", desc: "Surface accounts ripe for negotiated settlements." },
  { icon: BarChart3, title: "Executive Dashboard", desc: "Live KPIs across outstanding, recovered and follow-ups." },
  { icon: ListChecks, title: "Team Work Queues", desc: "Assign and track follow-ups across managers & agents." },
];

function OutcomeCards() {
  return (
    <div className="mb-6 border border-border bg-card p-6" data-testid="import-outcome-cards">
      <div className="flex items-center gap-2">
        <h2 className="font-heading font-bold text-lg">What happens after you upload your Excel?</h2>
      </div>
      <p className="text-sm text-muted-foreground mt-1">RecoverOS reads your spreadsheet once and turns it into a live recovery command center — your original file stays untouched.</p>
      <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {OUTCOMES.map((o) => (
          <div key={o.title} className="flex items-start gap-3 border border-border rounded-sm p-3 bg-secondary/30" data-testid={`outcome-card-${o.title.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}`}>
            <div className="h-9 w-9 rounded-sm bg-[#0B3D91]/10 flex items-center justify-center shrink-0">
              <o.icon className="h-4 w-4 text-[#0B3D91]" strokeWidth={2.2} />
            </div>
            <div>
              <div className="text-sm font-semibold text-foreground leading-tight">{o.title}</div>
              <div className="text-xs text-muted-foreground mt-0.5">{o.desc}</div>
            </div>
          </div>
        ))}
      </div>
      <div className="mt-4 flex items-center gap-1.5 text-xs text-muted-foreground">
        <Lock className="h-3.5 w-3.5 text-[#16A34A]" />
        Your data stays in your own isolated workspace — never shared across tenants.
      </div>
    </div>
  );
}

const STEPS = ["Upload", "Map Columns", "Validate & Fix", "Done"];
const NONE = "__none__";

export default function ImportWizard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const fileRef = useRef(null);
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [parse, setParse] = useState(null);
  const [mapping, setMapping] = useState({});
  const [validation, setValidation] = useState(null);
  const [validating, setValidating] = useState(false);
  const [result, setResult] = useState(null);
  const [saveMapping, setSaveMapping] = useState(true);
  const [dedupe, setDedupe] = useState("skip");
  const [uploadPct, setUploadPct] = useState(0);
  const [status, setStatus] = useState(null);

  useEffect(() => { api.get("/import/status").then((r) => setStatus(r.data)).catch(() => {}); }, []);
  useEffect(() => { logEvent("import_screen_viewed", {}); }, []);
  useEffect(() => { if (step === 3) logEvent("analyzer_viewed", { source: "import_done" }); }, [step]);

  const targetFields = parse?.target_fields || [];
  const requiredUnmapped = targetFields.filter((f) => f.required && !mapping[f.key]);

  const runValidate = async (m, sid = parse?.session_id) => {
    const { data } = await api.post("/import/validate", { session_id: sid, mapping: m });
    return data;
  };

  const onFile = async (file) => {
    if (!file) return;
    const fileType = (file.name.split(".").pop() || "").toLowerCase();
    logEvent("file_selected", { file_type: fileType, size_bytes: file.size });
    logEvent("upload_started", { file_type: fileType, size_bytes: file.size });
    setBusy(true);
    setUploadPct(0);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/import/parse", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (e) => { if (e.total) setUploadPct(Math.round((e.loaded / e.total) * 100)); },
      });
      setParse(data);
      const m = data.suggested_mapping || {};
      setMapping(m);
      const reqOk = (data.target_fields || []).filter((f) => f.required).every((f) => m[f.key]);
      if (data.has_saved_mapping && reqOk) {
        // Repeat importer: skip the mapping step entirely and validate.
        setValidating(true);
        setValidation(await runValidate(m, data.session_id));
        logEvent("mapping_confirmed", { source: "saved_mapping" });
        setStep(2);
        toast.success("Applied your saved column mapping");
      } else {
        setStep(1);
      }
    } catch (e) {
      logEvent("upload_failed", { stage: "parse", error: (e?.response?.data?.detail || e?.message || "parse_error").toString().slice(0, 200) });
      toast.error(e?.response?.data?.detail || "Could not read this file");
    } finally {
      setBusy(false);
      setValidating(false);
    }
  };

  const toValidate = async () => {
    if (requiredUnmapped.length) { toast.error(`Please map: ${requiredUnmapped.map((f) => f.label).join(", ")}`); return; }
    setBusy(true);
    setValidating(true);
    setValidation(null);
    setStep(2);
    try {
      setValidation(await runValidate(mapping));
      logEvent("mapping_confirmed", {});
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Validation failed");
      setStep(1);
    } finally {
      setBusy(false);
      setValidating(false);
    }
  };

  const applyEdits = async (edits) => {
    const { data } = await api.patch(`/import/session/${parse.session_id}/rows`, { mapping, edits });
    setValidation(data);
    return data;
  };

  const applyFix = async (finding) => {
    await applyEdits([{ row_index: finding.row_index, field_key: finding.field_key, value: finding.autofix_value }]);
  };

  const autofixAll = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/import/session/${parse.session_id}/autofix`, { mapping });
      setValidation(data);
      toast.success(`Auto-fixed ${data.autofixed || 0} issue${(data.autofixed || 0) === 1 ? "" : "s"}`);
    } catch (e) {
      toast.error("Auto-fix failed");
    } finally {
      setBusy(false);
    }
  };

  const replaceFile = async (file) => {
    if (!file) return;
    setBusy(true);
    setValidating(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/import/parse", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setParse(data);
      const newHeaders = data.headers || [];
      const kept = {};
      Object.entries(mapping).forEach(([k, v]) => { if (newHeaders.includes(v)) kept[k] = v; });
      const merged = { ...(data.suggested_mapping || {}), ...kept };
      setMapping(merged);
      const reqOk = (data.target_fields || []).filter((f) => f.required).every((f) => merged[f.key]);
      if (reqOk) {
        setValidation(await runValidate(merged, data.session_id));
        setStep(2);
        toast.success("File replaced — re-validated with your mapping");
      } else {
        setStep(1);
        toast.message("Please re-map the required columns for the new file");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not read this file");
    } finally {
      setBusy(false);
      setValidating(false);
    }
  };

  const downloadErrorReport = async () => {
    try {
      const r = await api.get(`/import/session/${parse.session_id}/error-report`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = "recoveros_import_issues.xlsx";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Could not generate the error report");
    }
  };

  const runImport = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/import/commit", { session_id: parse.session_id, mapping, dry_run: false, save_mapping: saveMapping, dedupe });
      setResult(data);
      saveImportHealth(validation?.summary);
      logEvent("ExcelUploaded", { created: data?.created || 0, updated: data?.updated || 0 });
      logEvent("upload_completed", { created: data?.created || 0, updated: data?.updated || 0 });
      try {
        const uid = JSON.parse(localStorage.getItem("user") || "{}")?.user_id;
        localStorage.setItem(`recoveros_excel_uploaded_${uid || ""}`, "1");
      } catch (e) { /* noop */ }
      window.dispatchEvent(new Event("recoveros:usage-changed"));
      window.dispatchEvent(new Event("recoveros:setup-changed"));
      setStep(3);
      window.dispatchEvent(new Event("recoveros:usage-changed"));
    } catch (e) {
      logEvent("upload_failed", { stage: "commit", error: (e?.response?.data?.detail || e?.message || "commit_error").toString().slice(0, 200) });
      toast.error(e?.response?.data?.detail || "Import failed");
    } finally {
      setBusy(false);
    }
  };

  const downloadTemplate = async () => {
    try {
      const r = await api.get("/import/template", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = "recoveros_import_template.xlsx";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Could not download template");
    }
  };

  return (
    <div data-testid="import-wizard-page" className="max-w-5xl">
      <PageHeader title="Import Your Accounts" subtitle="Upload an Excel or CSV file and we'll create your accounts in minutes." />
      <Stepper step={step} />

      {step === 0 && <OutcomeCards />}
      {step === 0 && <UploadStep busy={busy} uploadPct={uploadPct} fileRef={fileRef} onFile={onFile} onTemplate={downloadTemplate} status={status}
        showSample={["client_admin", "manager"].includes(user?.role) && (status?.total_accounts ?? 0) === 0}
        onSample={() => exploreSampleAndGo()} />}
      {step === 1 && (
        <MapStep
          parse={parse} mapping={mapping} setMapping={setMapping}
          targetFields={targetFields} requiredUnmapped={requiredUnmapped}
          busy={busy} onBack={() => setStep(0)} onNext={toValidate}
        />
      )}
      {step === 2 && (
        <ValidateStep
          validation={validation} validating={validating} busy={busy}
          dedupe={dedupe} onDedupe={setDedupe}
          saveMapping={saveMapping} setSaveMapping={setSaveMapping}
          onBack={() => setStep(1)} onImport={runImport}
          onEdit={applyEdits} onApplyFix={applyFix} onAutofix={autofixAll}
          onReplaceFile={replaceFile} onDownloadReport={downloadErrorReport}
        />
      )}
      {step === 3 && <DoneStep result={result} health={validation?.summary} onGo={() => navigate("/dashboard")} />}
    </div>
  );
}

function Stepper({ step }) {
  return (
    <div className="flex items-center gap-2 mb-6" data-testid="import-stepper">
      {STEPS.map((s, i) => (
        <div key={s} className="flex items-center gap-2">
          <div className={`flex items-center gap-2 px-3 py-1.5 rounded-sm text-sm font-semibold border ${
            i === step ? "bg-[#0B3D91] text-white border-[#0B3D91]"
            : i < step ? "bg-[#16A34A]/10 text-[#16A34A] border-[#16A34A]/30"
            : "bg-card text-muted-foreground border-border"}`}>
            {i < step ? <Check className="h-3.5 w-3.5" /> : <span className="text-xs">{i + 1}</span>} {s}
          </div>
          {i < STEPS.length - 1 && <div className="w-6 h-px bg-border" />}
        </div>
      ))}
    </div>
  );
}

function UploadStep({ busy, uploadPct, fileRef, onFile, onTemplate, status, showSample, onSample }) {
  const [drag, setDrag] = useState(false);
  return (
    <div className="border border-border bg-card p-8">
      {showSample && (
        <div className="mb-5 flex flex-wrap items-center gap-3 border border-[#0B3D91]/25 bg-[#0B3D91]/[0.04] px-4 py-3 rounded-sm" data-testid="import-sample-banner">
          <FolderOpen className="h-5 w-5 text-[#0B3D91] shrink-0" />
          <div className="flex-1 min-w-[220px]">
            <div className="text-sm font-semibold text-foreground">New here? Explore a sample portfolio first</div>
            <div className="text-xs text-muted-foreground">See RecoverOS in action with 50 demo accounts — then import your own in one click.</div>
          </div>
          <button type="button" onClick={onSample}
            className="inline-flex items-center gap-1.5 rounded-sm border-2 border-[#0B3D91] text-[#0B3D91] px-4 py-2 text-sm font-semibold hover:bg-[#0B3D91]/[0.06] transition-colors shrink-0"
            data-testid="import-explore-sample-btn">
            <Sparkles className="h-4 w-4" /> Explore Sample Portfolio
          </button>
        </div>
      )}
      {status?.last_import_at && (
        <div className="mb-5 flex flex-wrap items-center gap-2 text-sm bg-secondary/60 border border-border px-4 py-2.5 rounded-sm" data-testid="last-import-banner">
          <Clock className="h-4 w-4 text-[#0B3D91]" />
          <span>Last imported on <strong>{new Date(status.last_import_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}</strong> · <strong>{status.last_import_count}</strong> account{status.last_import_count === 1 ? "" : "s"}</span>
          <span className="text-muted-foreground">· {status.total_accounts} total in portfolio</span>
          <span className="ml-auto text-xs text-muted-foreground">Re-upload below to refresh balances</span>
        </div>
      )}
      <div
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={(e) => { e.preventDefault(); setDrag(false); onFile(e.dataTransfer.files?.[0]); }}
        onClick={() => { logEvent("upload_cta_clicked", { location: "dropzone" }); fileRef.current?.click(); }}
        className={`cursor-pointer rounded-sm border-2 border-dashed p-12 text-center transition-colors ${
          drag ? "border-[#0B3D91] bg-[#0B3D91]/5" : "border-border hover:border-[#0B3D91]/40"}`}
        data-testid="import-dropzone"
      >
        <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" className="hidden"
          onChange={(e) => onFile(e.target.files?.[0])} data-testid="import-file-input" />
        {busy ? (
          <div className="flex flex-col items-center gap-3 text-muted-foreground">
            <Loader2 className="h-10 w-10 animate-spin text-[#0B3D91]" />
            <p className="text-sm">{uploadPct > 0 && uploadPct < 100 ? `Uploading… ${uploadPct}%` : "Reading your file…"}</p>
            <div className="h-1.5 w-56 bg-border rounded-full overflow-hidden" data-testid="upload-progress">
              <div className="h-full bg-[#0B3D91] transition-all duration-200" style={{ width: `${uploadPct < 100 ? uploadPct : 100}%` }} />
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-3">
            <div className="h-14 w-14 rounded-full bg-[#0B3D91]/10 flex items-center justify-center">
              <UploadCloud className="h-7 w-7 text-[#0B3D91]" />
            </div>
            <p className="font-heading font-bold text-lg">Drop your file here or click to browse</p>
            <p className="text-sm text-muted-foreground">Excel (.xlsx, .xls) or CSV · up to 10,000 rows</p>
            <Button className="mt-2 rounded-sm bg-[#0B3D91] hover:bg-[#0a3578]" data-testid="import-browse-btn">
              <FileSpreadsheet className="h-4 w-4 mr-1.5" /> Choose File
            </Button>
          </div>
        )}
      </div>
      <p className="text-xs text-muted-foreground mt-4">
        Mandatory columns: <strong>Loan / Account ID</strong>, <strong>Customer Name</strong>, <strong>Loan Amount</strong>, <strong>Outstanding Amount</strong>, and <strong>Payment Due Date</strong> (DPD is auto-calculated from the Payment Due Date). Everything else is optional — you'll map columns on the next step.
      </p>
      <div className="mt-3 flex items-center gap-2 text-sm">
        <span className="text-muted-foreground">Not sure of the format?</span>
        <button type="button" onClick={(e) => { e.stopPropagation(); logEvent("template_downloaded", {}); onTemplate(); }} className="inline-flex items-center gap-1.5 font-semibold text-[#0B3D91] hover:underline" data-testid="import-template-btn">
          <FileDown className="h-4 w-4" /> Download sample template
        </button>
      </div>

      {/* Secondary path — for users without a file ready. Excel upload above remains primary. */}
      <div className="mt-6 pt-5 border-t border-border">
        <div className="flex items-center gap-3 text-xs font-semibold uppercase tracking-widest text-muted-foreground">
          <span className="h-px flex-1 bg-border" /> OR <span className="h-px flex-1 bg-border" />
        </div>
        <div className="mt-4 flex flex-wrap items-center justify-center gap-3 text-center">
          <span className="text-sm text-muted-foreground">Don't have a file ready? Start with one customer.</span>
          <Button type="button" variant="outline" className="rounded-sm gap-1.5 border-2 border-[#0B3D91] text-[#0B3D91] hover:bg-[#0B3D91]/[0.05]"
            onClick={() => window.dispatchEvent(new CustomEvent("recoveros:quick-add", { detail: { source: "import" } }))}
            data-testid="import-add-one-btn">
            <Plus className="h-4 w-4" /> Add one account
          </Button>
        </div>
      </div>
    </div>
  );
}

function MapStep({ parse, mapping, setMapping, targetFields, requiredUnmapped, busy, onBack, onNext }) {
  const headers = parse?.headers || [];
  const setField = (key, val) => setMapping((m) => {
    const next = { ...m };
    if (val === NONE) delete next[key]; else next[key] = val;
    return next;
  });
  return (
    <div className="border border-border bg-card p-6" data-testid="import-map-step">
      {parse?.has_saved_mapping && (
        <div className="mb-4 flex items-center gap-2 text-sm bg-[#16A34A]/10 text-[#138a3e] px-3 py-2 rounded-sm" data-testid="saved-mapping-note">
          <Check className="h-4 w-4" /> We pre-filled this from your saved mapping. Adjust if needed.
        </div>
      )}
      <p className="text-sm text-muted-foreground mb-4">
        We auto-matched your columns. Review and adjust where needed. <span className="text-destructive">*</span> = required.
      </p>
      <div className="grid sm:grid-cols-2 gap-x-6 gap-y-3">
        {targetFields.map((f) => (
          <div key={f.key} className="flex items-center justify-between gap-3">
            <label className="text-sm font-medium shrink-0 w-44">
              {f.label} {f.required && <span className="text-destructive">*</span>}
              {f.is_custom && <span className="ml-1 text-[10px] font-semibold text-[#0B3D91] bg-[#0B3D91]/10 px-1.5 py-0.5 rounded-sm align-middle">CUSTOM</span>}
            </label>
            <Select value={mapping[f.key] || NONE} onValueChange={(v) => setField(f.key, v)}>
              <SelectTrigger className="rounded-sm" data-testid={`map-${f.key}`}>
                <SelectValue placeholder="— not mapped —" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NONE}>— not mapped —</SelectItem>
                {headers.map((h) => <SelectItem key={h} value={h}>{h}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        ))}
      </div>
      <div className="flex items-center justify-between mt-6">
        <Button variant="outline" className="rounded-sm gap-1" onClick={onBack}><ArrowLeft className="h-4 w-4" /> Back</Button>
        <Button className="rounded-sm gap-1 bg-[#0B3D91] hover:bg-[#0a3578]" onClick={onNext} disabled={busy || requiredUnmapped.length > 0} data-testid="import-to-preview-btn">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Validate &amp; Continue <ArrowRight className="h-4 w-4" /></>}
        </Button>
      </div>
    </div>
  );
}
function DoneStep({ result, health, onGo }) {
  return (
    <div data-testid="import-done-step">
      <div className="border border-border bg-card p-8 text-center">
        <div className="h-14 w-14 rounded-full bg-[#16A34A]/10 flex items-center justify-center mx-auto">
          <CheckCircle2 className="h-8 w-8 text-[#16A34A]" />
        </div>
        <h2 className="font-heading font-bold text-2xl mt-4" data-testid="import-done-headline">
          Your receivables analysis is ready
        </h2>
        <p className="text-sm text-muted-foreground mt-1" data-testid="import-done-confirmation">
          {result?.updated > 0
            ? `${fmtNum(result?.created || 0)} account(s) created · ${fmtNum(result.updated)} refreshed`
            : `${fmtNum(result?.created || 0)} account(s) imported successfully`}
          {result?.duplicates_in_file + result?.duplicates_existing > 0 && ` · ${result.duplicates_in_file + result.duplicates_existing} duplicate(s) skipped`}
          {result?.error_count > 0 && ` · ${result.error_count} error row(s) skipped`}
          . Here's where your money is stuck — and what to do next.
        </p>
        {(result?.dpd_calculated != null) && (
          <div className="mt-4 inline-flex flex-wrap items-center justify-center gap-2 text-sm bg-[#0B3D91]/5 border border-[#0B3D91]/20 px-4 py-2 rounded-sm" data-testid="import-dpd-banner">
            <span className="font-semibold text-[#0B3D91]">Canonical DPD auto-calculated</span>
            <span className="text-muted-foreground">for <strong>{fmtNum(result.dpd_calculated)}</strong> account(s) from their Payment Due Date{result?.dpd_unknown > 0 ? <> · <strong>{fmtNum(result.dpd_unknown)}</strong> Unknown (missing Payment Due Date)</> : ""}.</span>
          </div>
        )}
        <div className="flex justify-center"><ImportHealthCard summary={health} /></div>
        <Button onClick={onGo} className="mt-5 rounded-sm gap-1.5 bg-[#0B3D91] hover:bg-[#0a3578]" data-testid="import-go-dashboard-btn">
          <LayoutDashboard className="h-4 w-4" /> Go to Dashboard
        </Button>
      </div>

      {/* Instant portfolio insights — leads with the value hero (Total Outstanding / Stuck >90 / Start Here) */}
      <div className="mt-6">
        <InsightsPanel hero />
      </div>
    </div>
  );
}
